import { Interval } from "luxon";

import { ContactMatchingPhoneNumberResult } from "../Api/CallCenter/Responses/ContactMatchingPhoneNumberResult";
import { DateTime } from "../Helpers/DateTime";
import { UpcomingAppointment } from "../Models/Appointments/UpcomingAppointment";
import { ContactFacetType } from "../Models/Individuals/Enums/ContactFacetType";
import { LuxonDateTimeParser } from "./DateAndTime/LuxonDateTimeParser";

export default class ContactMatchingPhoneNumberResultSorter {
    private readonly CurrentDate: DateTime;

    constructor(currentDate: DateTime) {
        this.CurrentDate = currentDate;
    }

    private getAbsoluteDurationFromAppointment(appointment: UpcomingAppointment): number {
        if (appointment == null) {
            return Infinity;
        }
        const appointmentDate = LuxonDateTimeParser.parse(appointment.DateTime);
        const duration = (this.CurrentDate < appointmentDate
            ? Interval.fromDateTimes(this.CurrentDate, appointmentDate)
            : Interval.fromDateTimes(appointmentDate, this.CurrentDate)).toDuration();
        return Math.abs(duration.toMillis());
    }

    private getMinAbsoluteDurationFromAppointments(appointments: UpcomingAppointment[]): number {
        return appointments
            .map<number>(appt => this.getAbsoluteDurationFromAppointment(appt))
            .reduce<number>((prev, cur) => Math.min(prev, cur), Infinity);
    }

    private compareStrings(left: string, right: string): number {
        const leftFirst = -1;
        const rightFirst = 1;
        if (left == right) {
            return 0;
        }
        if (left == null) {
            return rightFirst;
        }
        if (right == null) {
            return leftFirst;
        }
        return left.localeCompare(right);
    }

    public SortResults(results: ContactMatchingPhoneNumberResult[]): ContactMatchingPhoneNumberResult[] {
        if (results == null) {
            return [];
        }
        // order results by:
        const sortedResults = [...results].sort((left, right) => {
            const leftFirst = -1;
            const rightFirst = 1;
            //  1. patients before relative
            if (left.Individual.ContactFacetType != right.Individual.ContactFacetType) {
                if (left.Individual.ContactFacetType == ContactFacetType.Patient) {
                    return leftFirst;
                }
                return rightFirst;
            }
            //  2. how far away from an appointment (past or future)
            const leftDiff = this.getMinAbsoluteDurationFromAppointments([left.NextUpcomingAppointment, left.LastUpcomingAppointment]);
            const rightDiff = this.getMinAbsoluteDurationFromAppointments([right.NextUpcomingAppointment, right.LastUpcomingAppointment]);
            if (leftDiff < rightDiff) {
                return leftFirst;
            }
            if (rightDiff < leftDiff) {
                return rightFirst;
            }
            //  3. last name
            const lastNameCompare = this.compareStrings(left.Individual.LastName, right.Individual.LastName);
            if (lastNameCompare != 0) {
                return lastNameCompare;
            }
            //  4. first name
            return this.compareStrings(left.Individual.FirstName, right.Individual.FirstName);
        });
        return sortedResults;
    }
}
